import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';

// Redux
import { setOrderDataAction } from '../../redux/Actions/CreateOrderActions';
import { setSelectedServiceAddressAction } from '../../redux/Actions/ServiceAddressesActions';
import { getOrderPreferencesApi } from '../../redux/Reducers/MyAccountReducer';

// Components
import Loader from '../../components/Loader';
import NoData from '../../components/NoData';
import ServiceAddressModal from './PickupAddress/ServiceAddressModal';
import PromoSurpriseModal from './PromoSurpriseModal';

// ─── Constants ──────────────────────────────────────────────────────────────────
const VISIBLE_COUNT = 4; // show first N addresses, rest behind "Show more"

// ─── StepPickupAddress ──────────────────────────────────────────────────────────
export default function StepPickupAddress({ onContinue }) {
  const dispatch = useDispatch();
  const { allServiceAddresses, selectedAddress } = useSelector((s) => s.serviceAddresses);
  const { orderServiceAddress } = useSelector((s) => s.createNewOrder);
  const { profileData } = useSelector((s) => s.profile);

  const [loading, setLoading] = useState(false);
  const [newAddressModal, setNewAddressModal] = useState(false);
  const [addressModalType, setAddressModalType] = useState('new');
  const [editAddressId, setEditAddressId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [promoAddressId, setPromoAddressId] = useState(null);

  // ── Select an address (userClick distinguishes manual vs auto) ────
  const selectAddr = useCallback(
    (address, userClick = false) => {
      setLoading(true);
      dispatch(setSelectedServiceAddressAction(address));
      dispatch(setOrderDataAction({ selectedServices: [] }));
      const pref = dispatch(getOrderPreferencesApi(address?.id));
      pref?.then((res) => {
        setLoading(false);
        if (res?.status === 200 || res?.status === 201) {
          dispatch(
            setOrderDataAction({
              orderServiceAddress: address || res?.data?.preferred_service_address,
              orderProvider: res?.data?.preferred_worker || null,
            })
          );
          if (userClick && address?.id) {
            setPromoAddressId(address.id);
            setPromoModalOpen(true);
          }
        }
      });
    },
    [dispatch]
  );

  // Auto-select default
  useEffect(() => {
    if (!orderServiceAddress && selectedAddress) selectAddr(selectedAddress);
  }, [selectedAddress]);

  // Open add-address modal if no addresses
  useEffect(() => {
    if (isEmpty(allServiceAddresses) && selectedAddress != null) setNewAddressModal(true);
  }, [allServiceAddresses]);

  // ── Derived ───────────────────────────────────────────────────────
  const addresses = [...(allServiceAddresses || [])].sort((a, b) => {
    const aDisabled = a?.status === 'INACTIVE' || !a?.is_address_in_active_service_area ? 1 : 0;
    const bDisabled = b?.status === 'INACTIVE' || !b?.is_address_in_active_service_area ? 1 : 0;
    return aDisabled - bDisabled;
  });
  const visibleAddresses = showAll ? addresses : addresses.slice(0, VISIBLE_COUNT);
  const hasMore = addresses.length > VISIBLE_COUNT;

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div>
      {loading ? (
        <Loader text='Loading addresses...' classes='h-32' bg />
      ) : isEmpty(addresses) ? (
        <NoData text='No service addresses found' classes='h-20' />
      ) : (
        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
          {visibleAddresses.map((addr, i) => {
            const disabled = addr?.status === 'INACTIVE' || !addr?.is_address_in_active_service_area;
            const selected = orderServiceAddress?.id === addr?.id;

            return (
              <div
                key={addr?.id ?? i}
                className={`rounded-lg border-2 p-3 transition ${
                  disabled
                    ? 'pointer-events-none border-slate-100 bg-slate-50 opacity-40'
                    : selected
                    ? 'border-client-300 cursor-pointer bg-blue-50/30'
                    : 'cursor-pointer border-slate-200 bg-white hover:border-slate-300'
                }`}
                onClick={() =>
                  !disabled &&
                  selectAddr(
                    {
                      id: addr?.id,
                      full_address: addr?.full_address,
                      name: addr?.name,
                      time_zone: addr?.time_zone,
                    },
                    true
                  )
                }
              >
                <div className='flex items-start gap-3'>
                  {/* Radio */}
                  <input
                    type='radio'
                    name='pickupAddress'
                    checked={selected}
                    readOnly
                    className='text-client-300 focus:ring-client-300 mt-1 h-4 w-4 shrink-0 cursor-pointer'
                  />

                  {/* Content */}
                  <div className='min-w-0 flex-1'>
                    {/* Name */}
                    <p className='text-sm font-bold text-slate-900'>{addr?.name}</p>

                    {/* Full address */}
                    <p className='mt-0.5 text-sm leading-snug text-slate-600'>{addr?.full_address}</p>

                    {/* Status badges */}
                    {addr?.status === 'INACTIVE' && (
                      <span className='mt-1 inline-block rounded bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600'>
                        Inactive
                      </span>
                    )}
                    {!addr?.is_address_in_active_service_area && addr?.status !== 'INACTIVE' && (
                      <span className='mt-1 inline-block rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700'>
                        Not in service area
                      </span>
                    )}

                    {/* Action links */}
                    {!disabled && (
                      <div className='mt-1.5 flex items-center gap-3 text-xs'>
                        <button
                          type='button'
                          className='text-client-link font-semibold hover:underline'
                          onClick={(e) => {
                            e.stopPropagation();
                            setAddressModalType('edit');
                            setEditAddressId(addr?.id);
                            setNewAddressModal(true);
                          }}
                        >
                          Edit address
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Show more / less toggle */}
      {hasMore && (
        <button
          type='button'
          className='text-client-link mt-1 flex items-center gap-1 text-xs font-semibold hover:underline'
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? 'Show fewer addresses' : `Show more addresses`}
          {showAll ? <ChevronUpIcon className='h-3.5 w-3.5' /> : <ChevronDownIcon className='h-3.5 w-3.5' />}
        </button>
      )}

      {/* Add new address link */}
      {profileData?.billing_type !== 'SEPARATE' && profileData?.is_allowed_to_add_address && (
        <button
          type='button'
          className='text-client-link mt-3 flex items-center gap-1.5 text-xs font-semibold hover:underline'
          onClick={() => {
            setAddressModalType('new');
            setNewAddressModal(true);
          }}
        >
          <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M12 4v16m8-8H4' />
          </svg>
          Add a new service address
        </button>
      )}

      {/* Continue button */}
      <div className='mt-4 flex justify-end'>
        <button
          type='button'
          className={`rounded-full px-6 py-2 text-sm font-semibold text-white transition ${
            !orderServiceAddress ? 'cursor-not-allowed bg-slate-300' : 'bg-client-300 hover:bg-client-400 shadow'
          }`}
          onClick={onContinue}
          disabled={!orderServiceAddress}
        >
          Continue
        </button>
      </div>

      {/* Address modal (add / edit) */}
      <ServiceAddressModal
        newAddressModalOpen={newAddressModal}
        setNewAddressModalOpen={setNewAddressModal}
        addressModalType={addressModalType}
        selectedAddressId={editAddressId}
      />

      {/* Promo surprise modal */}
      <PromoSurpriseModal addressId={promoAddressId} open={promoModalOpen} onClose={() => setPromoModalOpen(false)} />
    </div>
  );
}
